import { QueryClient, useQuery } from '@tanstack/react-query';
import { QueryKeys } from '../../api/queryKeys';
import { REFERENCE_DATA_STALE_TIME } from '../../queryClient';
import { useInvalidatingMutation } from '../../api/useInvalidatingMutation';
import { useUserSettingsQuery } from '../profile/queryHooks';
import { selectManufacturingLocation } from '../profile/utils';
import {
  ManufacturingLocation,
  MCellCollection,
} from '../manufacturingLocation/types';
import { EMPTY_GUID } from '../../utils/string-utils';
import {
  addComment,
  changeSeverity,
  createDowntime,
  createDowntimeReason,
  createDowntimeReasonSupportGroupMapping,
  deleteDowntime,
  deleteDowntimeReason,
  deleteDowntimeReasonSupportGroupMapping,
  endDowntime,
  getCurrentDowntimes,
  getDowntimeById,
  getDowntimeReasons,
  getDowntimeReasonSupportGroupMappings,
  getDowntimes,
  getEscalationAssignments,
  markHelpArrived,
  requestHelp,
  updateDowntimeReason,
  updateDowntimeReasonById,
  updateDowntimeReasonSupportGroupMapping,
} from './api';
import {
  Downtime,
  DowntimeReasonSupportGroupMapping,
  DowntimeReasonSupportGroupMappingInfo,
} from './types';

type DowntimeListScope = 'current' | 'all';

/**
 * Fetches an Issue list for the user's selected manufacturing location, once the settings have loaded.
 */
const useDowntimeListQuery = (
  scope: DowntimeListScope,
  fetchDowntimes: typeof getDowntimes,
) => {
  const { data: location } = useUserSettingsQuery(selectManufacturingLocation);
  return useQuery({
    queryKey: [...QueryKeys.downtimeLists(), scope, location],
    queryFn: () =>
      fetchDowntimes(
        location!.mCellId,
        location!.inventoryCenterId,
        location!.mCellCollectionId,
      ),
    enabled: !!location,
    meta: { errorMessageKey: 'errorFetchingIssues' },
  });
};

/**
 * Fetches the current (not completed) Issues of the selected manufacturing location.
 */
export const useCurrentDowntimesQuery = () =>
  useDowntimeListQuery('current', getCurrentDowntimes);

/**
 * Fetches all Issues of the past year (comments excluded) of the selected manufacturing location.
 */
export const useDowntimesQuery = () =>
  useDowntimeListQuery('all', getDowntimes);

/**
 * Fetches an Issue by id.
 */
export const useDowntimeQuery = (id: string | undefined) =>
  useQuery({
    queryKey: QueryKeys.downtimeDetail(id!),
    queryFn: () => getDowntimeById(id!),
    enabled: !!id,
    meta: { errorMessageKey: 'errorFetchingIssueDetail' },
  });

/**
 * Fetches the escalation assignments of an Issue.
 */
export const useDowntimeEscalationAssignmentsQuery = (
  downtimeId: string | undefined,
) =>
  useQuery({
    queryKey: [
      ...QueryKeys.downtimeDetail(downtimeId!),
      'escalationAssignments',
    ],
    queryFn: () => getEscalationAssignments(downtimeId!),
    enabled: !!downtimeId,
    meta: { errorMessageKey: 'errorFetchingIssueEscalations' },
  });

/**
 * Fetches the Issue Reasons of an inventory center (EMPTY_GUID for all inventory centers).
 * Stays fresh for 5 minutes, since grid cells call it per row; mutations and SignalR invalidate it.
 */
export const useDowntimeReasonsQuery = (
  inventoryCenterId: string | undefined,
) =>
  useQuery({
    queryKey: [QueryKeys.DOWNTIME_REASONS, inventoryCenterId],
    queryFn: () => getDowntimeReasons(inventoryCenterId!),
    enabled: !!inventoryCenterId,
    staleTime: REFERENCE_DATA_STALE_TIME,
    meta: { errorMessageKey: 'errorFetchingIssueReasons' },
  });

/**
 * Fetches all Issue Reason Support Group Mappings.
 */
export const useDowntimeReasonSupportGroupMappingsQuery = () =>
  useQuery({
    queryKey: [QueryKeys.DOWNTIME_REASON_SUPPORT_GROUP_MAPPINGS],
    queryFn: getDowntimeReasonSupportGroupMappings,
    meta: { errorMessageKey: 'errorFetchingIssueReasonSupportGroupMappings' },
  });

/** Creates an Issue. */
export const useCreateDowntimeMutation = () =>
  useInvalidatingMutation(
    QueryKeys.downtimeLists(),
    createDowntime,
    'errorCreatingIssue',
  );

/** Deletes an Issue. Refetches only the lists: refetching the deleted Issue would fail. */
export const useDeleteDowntimeMutation = () =>
  useInvalidatingMutation(
    QueryKeys.downtimeLists(),
    deleteDowntime,
    'errorDeletingIssue',
  );

/** Requests help on an Issue (unused: Issues are created with help requested). */
export const useRequestHelpMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIMES],
    requestHelp,
    'errorRequestingHelp',
  );

/** Marks help arrived on an Issue. */
export const useMarkHelpArrivedMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIMES],
    markHelpArrived,
    'errorMarkingHelpArrived',
  );

/** Ends an Issue. */
export const useEndDowntimeMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIMES],
    endDowntime,
    'errorEndingIssue',
  );

/** Changes the severity of an Issue. */
export const useChangeSeverityMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIMES],
    changeSeverity,
    'errorChangingSeverity',
  );

/** Changes the reason, description and asset of an Issue. */
export const useUpdateDowntimeReasonMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIMES],
    updateDowntimeReason,
    'errorUpdatingIssueReason',
  );

/** Adds a comment to an Issue. */
export const useAddCommentMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIMES],
    addComment,
    'errorAddingComment',
  );

/** Creates an Issue Reason. */
export const useCreateDowntimeReasonMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIME_REASONS],
    createDowntimeReason,
    'errorCreatingIssueReason',
  );

/** Updates an Issue Reason. */
export const useUpdateDowntimeReasonByIdMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIME_REASONS],
    updateDowntimeReasonById,
    'errorUpdatingIssueReason',
  );

/** Deletes an Issue Reason. */
export const useDeleteDowntimeReasonMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIME_REASONS],
    deleteDowntimeReason,
    'errorDeletingIssueReason',
  );

/**
 * Maps a mapping's watcher joins to the support group ids the API expects.
 */
const watcherSupportGroupIds = (info: DowntimeReasonSupportGroupMappingInfo) =>
  info.watcherSupportGroupJoins?.map((w) => w.supportGroupId);

/** Creates an Issue Reason Support Group Mapping. */
export const useCreateDowntimeReasonSupportGroupMappingMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIME_REASON_SUPPORT_GROUP_MAPPINGS],
    (info: DowntimeReasonSupportGroupMappingInfo) =>
      createDowntimeReasonSupportGroupMapping({
        downtimeReasonId: info.downtimeReasonId,
        ownerSupportGroupId: info.ownerSupportGroupId,
        watcherSupportGroupIds: watcherSupportGroupIds(info),
      }),
    'errorCreatingIssueReasonSupportGroupMapping',
  );

/** Updates an Issue Reason Support Group Mapping, optimistically updating the cached list. */
export const useUpdateDowntimeReasonSupportGroupMappingMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIME_REASON_SUPPORT_GROUP_MAPPINGS],
    ({
      id,
      downtimeReasonSupportGroupMappingInfo: info,
    }: {
      id: string;
      downtimeReasonSupportGroupMappingInfo: DowntimeReasonSupportGroupMappingInfo;
    }) =>
      updateDowntimeReasonSupportGroupMapping(id, {
        ownerSupportGroupId: info.ownerSupportGroupId,
        watcherSupportGroupIds: watcherSupportGroupIds(info),
      }),
    'errorUpdatingIssueReasonSupportGroupMapping',
    (
      mappings: DowntimeReasonSupportGroupMapping[],
      { id, downtimeReasonSupportGroupMappingInfo },
    ) =>
      mappings.map((m) =>
        m.id === id ? { id, ...downtimeReasonSupportGroupMappingInfo } : m,
      ),
  );

/** Deletes an Issue Reason Support Group Mapping. */
export const useDeleteDowntimeReasonSupportGroupMappingMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.DOWNTIME_REASON_SUPPORT_GROUP_MAPPINGS],
    deleteDowntimeReasonSupportGroupMapping,
    'errorDeletingIssueReasonSupportGroupMapping',
  );

/**
 * Whether an Issue belongs to a list's manufacturing location (EMPTY_GUID matches anything).
 * A collection matches when it is cached and contains the Issue's MCell, or when it isn't cached.
 */
const isInLocation = (
  queryClient: QueryClient,
  { mCellId, inventoryCenterId, mCellCollectionId }: ManufacturingLocation,
  downtime: Downtime,
) => {
  const collection = queryClient
    .getQueryData<MCellCollection[]>([QueryKeys.MCELL_COLLECTIONS])
    ?.find((c) => c.id === mCellCollectionId);
  return (
    (mCellId === EMPTY_GUID || mCellId === downtime.mCell.id) &&
    (inventoryCenterId === EMPTY_GUID ||
      inventoryCenterId === downtime.mCell.inventoryCenter.id) &&
    (mCellCollectionId === EMPTY_GUID ||
      !collection ||
      collection.mCellCollectionMCell.some(
        (m) => m.mCellId === downtime.mCell.id,
      ))
  );
};

/**
 * Replaces an Issue in a list, or inserts it in start-date-descending order.
 */
const upsertDowntime = (downtimes: Downtime[], downtime: Downtime) =>
  downtimes.some((d) => d.id === downtime.id)
    ? downtimes.map((d) => (d.id === downtime.id ? downtime : d))
    : [...downtimes, downtime].sort(
        (a, b) =>
          new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
      );

/**
 * Applies a SignalR `Downtime` push to the cached Issue lists and detail without any request:
 * created/updated Issues are upserted into the lists of matching locations (the current list only
 * while not completed) and into a cached detail; deleted Issues are removed from the lists.
 * @param queryClient
 * @param messageType - DowntimeCreated, DowntimeUpdated or DowntimeDeleted
 * @param payload - The Issue, or its id when deleted
 */
export const applyDowntimePush = (
  queryClient: QueryClient,
  messageType: string,
  payload: Downtime | string,
) => {
  const lists = queryClient.getQueriesData<Downtime[]>({
    queryKey: QueryKeys.downtimeLists(),
  });

  if (messageType === 'DowntimeDeleted') {
    lists.forEach(([queryKey, downtimes]) =>
      queryClient.setQueryData(
        queryKey,
        downtimes?.filter((d) => d.id !== payload),
      ),
    );
    return;
  }
  if (messageType !== 'DowntimeCreated' && messageType !== 'DowntimeUpdated') {
    return;
  }

  const downtime = payload as Downtime;
  lists.forEach(([queryKey, downtimes]) => {
    const [, , scope, location] = queryKey as [
      string,
      string,
      DowntimeListScope,
      ManufacturingLocation,
    ];
    if (!downtimes || !isInLocation(queryClient, location, downtime)) return;
    const belongs = scope === 'all' || downtime.status !== 'Completed';
    queryClient.setQueryData(
      queryKey,
      belongs
        ? upsertDowntime(downtimes, downtime)
        : downtimes.filter((d) => d.id !== downtime.id),
    );
  });
  queryClient.setQueryData<Downtime>(
    QueryKeys.downtimeDetail(downtime.id),
    (detail) => detail && downtime,
  );
};
